import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SimpleGrid, Card, CardBody, Stat, StatLabel, StatNumber, StatHelpText,
  Icon, HStack, VStack, Text, Badge,
} from '@chakra-ui/react';
import { FiUsers, FiLayers, FiGrid, FiPercent } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import staffService from '../services/staffService';
import roomTypeService from '../services/roomTypeService';
import roomService from '../services/roomService';
import taxRateService from '../services/taxRateService';

const StatCard = ({ label, value, icon, color, helpText }) => (
  <Card shadow="sm" borderWidth="1px">
    <CardBody>
      <HStack spacing={4}>
        <Icon as={icon} boxSize={8} color={`${color}.500`} />
        <Stat>
          <StatLabel>{label}</StatLabel>
          <StatNumber>{value}</StatNumber>
          {helpText && <StatHelpText>{helpText}</StatHelpText>}
        </Stat>
      </HStack>
    </CardBody>
  </Card>
);

const Overview = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ staff: 0, roomTypes: 0, rooms: 0, taxRates: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [staffData, roomTypesData, roomsData, taxData] = await Promise.allSettled([
          staffService.getAll(),
          roomTypeService.getAll(),
          roomService.getAll(),
          taxRateService.getAll(),
        ]);
        setStats({
          staff: staffData.status === 'fulfilled' ? (staffData.value?.results || staffData.value || []).length : 0,
          roomTypes: roomTypesData.status === 'fulfilled' ? (roomTypesData.value?.results || roomTypesData.value || []).length : 0,
          rooms: roomsData.status === 'fulfilled' ? (roomsData.value?.results || roomsData.value || []).length : 0,
          taxRates: taxData.status === 'fulfilled' ? (taxData.value?.results || taxData.value || []).length : 0,
        });
      } catch {
        // silently handle
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const hotelName = user?.user_profile?.hotel?.name || t('overview.hotelFallback');

  return (
    <PageShell
      title={t('overview.welcome', { name: user?.first_name || t('overview.adminFallback') })}
      subtitle={t('overview.subtitle', { hotel: hotelName })}
      loading={loading}
    >
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={8}>
        <StatCard label={t('overview.stats.staffMembers')} value={stats.staff} icon={FiUsers} color="blue" />
        <StatCard label={t('overview.stats.roomTypes')} value={stats.roomTypes} icon={FiLayers} color="teal" />
        <StatCard label={t('overview.stats.totalRooms')} value={stats.rooms} icon={FiGrid} color="green" />
        <StatCard label={t('overview.stats.taxRates')} value={stats.taxRates} icon={FiPercent} color="orange" />
      </SimpleGrid>

      <Card shadow="sm" borderWidth="1px">
        <CardBody>
          <VStack align="start" spacing={3}>
            <Text fontWeight="600" fontSize="md">{t('overview.quickLinks.title')}</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="100%">
              {[
                { labelKey: 'overview.quickLinks.staff', badgeKey: 'overview.quickLinks.badges.staff', color: 'blue', path: '/staff' },
                { labelKey: 'overview.quickLinks.settings', badgeKey: 'overview.quickLinks.badges.settings', color: 'purple', path: '/settings' },
                { labelKey: 'overview.quickLinks.roomTypes', badgeKey: 'overview.quickLinks.badges.roomTypes', color: 'teal', path: '/room-types' },
                { labelKey: 'overview.quickLinks.rooms', badgeKey: 'overview.quickLinks.badges.rooms', color: 'green', path: '/rooms' },
                { labelKey: 'overview.quickLinks.chargeableItems', badgeKey: 'overview.quickLinks.badges.chargeableItems', color: 'orange', path: '/charge-presets' },
                { labelKey: 'overview.quickLinks.taxRates', badgeKey: 'overview.quickLinks.badges.taxRates', color: 'red', path: '/tax-rates' },
                { labelKey: 'overview.quickLinks.guests', badgeKey: 'overview.quickLinks.badges.guests', color: 'cyan', path: '/guests' },
                { labelKey: 'overview.quickLinks.auditLogs', badgeKey: 'overview.quickLinks.badges.auditLogs', color: 'gray', path: '/audit-logs' },
              ].map((item, i) => (
                <HStack key={i} p={3} borderWidth="1px" borderRadius="md" cursor="pointer"
                  _hover={{ bg: 'gray.50' }} onClick={() => navigate(item.path)}>
                  <Badge colorScheme={item.color} fontSize="xs">{t(item.badgeKey)}</Badge>
                  <Text fontSize="sm" color="gray.600">{t(item.labelKey)}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>
    </PageShell>
  );
};

export default Overview;
